import { groupBy } from '@grubhubprod/umami-library/dist/nodash/groupBy';
import {
  MerchantRestrictionsResponseUI,
  ItemRestrictionsResponseUI,
  MerchantRestrictionPayload,
} from 'src/types/OrderRestrictions';
import { CorporateFormFields } from 'src/components/enterprise/features/Corporate/components/Form/types/CorporateForm';
import { DinerNotificationPreferenceResponseUI } from 'src/types/CorporateFieldsData';

export type RestrictionsFormData = Record<string, string | boolean>;

export type RestrictionItem = (
  | MerchantRestrictionsResponseUI
  | ItemRestrictionsResponseUI
  | DinerNotificationPreferenceResponseUI
) & {
  displayCategoryName?: string | null;
};

export type GroupRestrictions = Array<{
  categoryName: string;
  restrictions: RestrictionItem[];
}>;

export function groupRestrictions(list: RestrictionItem[]): GroupRestrictions {
  const groupedRestrictions = groupBy(
    list,
    (item) => item.displayCategoryName || ''
  );

  const groupedRestrictionsArr = Object.entries(groupedRestrictions).map(
    ([categoryName, restrictions]) => ({ categoryName, restrictions })
  );

  return groupedRestrictionsArr;
}

function createMerchantRestrictionIfSelected(
  categoryName: string,
  isSelected: boolean
): MerchantRestrictionPayload[] {
  if (isSelected) {
    return [{ type: categoryName }];
  }
  return [];
}

export function transformFormToPayloadMerchantRestrictions(
  merchantRestrictions: RestrictionsFormData
): MerchantRestrictionPayload[] {
  const merchantRestrictionsFormOptions = Object.entries(
    merchantRestrictions || {}
  );

  if (!merchantRestrictionsFormOptions.length) {
    return [];
  }

  const merchantRestrictionsList = merchantRestrictionsFormOptions.reduce(
    (prev, [categoryName, categorySelectionValue]) => [
      ...prev,
      ...createMerchantRestrictionIfSelected(
        categoryName,
        Boolean(categorySelectionValue)
      ),
    ],
    []
  );

  return merchantRestrictionsList;
}

export function transformPayloadToFormMerchantRestrictions(
  merchantRestrictions: MerchantRestrictionPayload[]
): RestrictionsFormData {
  if (!merchantRestrictions.length) {
    return {};
  }

  return merchantRestrictions.reduce(
    (merchantRestrictionsDictionary, { type: merchantRestrictionType }) => ({
      ...merchantRestrictionsDictionary,
      [merchantRestrictionType]: merchantRestrictionType,
    }),
    {}
  );
}

export function transformFormToPayloadItemRestrictions(
  itemRestrictions: RestrictionsFormData
): string[] {
  return Object.keys(itemRestrictions || {}).filter(
    (restrictionName) => itemRestrictions[restrictionName]
  );
}

export function transformPayloadToFormItemRestrictions(
  itemRestrictions: string[]
): RestrictionsFormData {
  return itemRestrictions.reduce(
    (prev, restrictionName) => ({
      ...prev,
      [restrictionName]: true,
    }),
    {}
  );
}

export function transformEntityItemRestrictions(
  entityItemRestrictions: string[]
): RestrictionItem[] {
  return entityItemRestrictions.map((type) => ({
    type,
    displayName: type,
    displayCategoryName: '',
    description: '',
  }));
}

export function requiredRestrictionsValidator(
  values: RestrictionsFormData
): true | string {
  return (
    Object.values(values).some(Boolean) || 'Selecting an option is required'
  );
}

export function cleanupFormRestrictionsFields(
  formValues: Partial<CorporateFormFields>
): Partial<CorporateFormFields> {
  const { enableItemRestrictions, enableMerchantRestrictions, ...rest } =
    formValues;
  return rest;
}
